/**
 * Leviathan Web - Supplier Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing suppliers
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface SupplierFormData {
  name: string;
  displayName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  email: string;
  phone: string;
  fax: string;
  website: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  taxId: string;
  accountNumber: string;
  paymentTerms: string;
  creditLimit: string;
  notes: string;
  isActive: boolean;
}

interface SupplierFormProps {
  supplierId?: string;
  initialData?: Partial<SupplierFormData>;
  onSuccess?: () => void;
}

export default function SupplierForm({ supplierId, initialData, onSuccess }: SupplierFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    displayName: '',
    primaryContactFirstName: '',
    primaryContactLastName: '',
    email: '',
    phone: '',
    fax: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    taxId: '',
    accountNumber: '',
    paymentTerms: '30',
    creditLimit: '',
    notes: '',
    isActive: true,
    ...initialData,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const payload: Record<string, unknown> = {
        ...formData,
        paymentTerms: formData.paymentTerms ? parseInt(formData.paymentTerms) : null,
        creditLimit: formData.creditLimit ? parseFloat(formData.creditLimit) : null,
      };

      // Remove empty strings, convert to null
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      const url = supplierId
        ? `${API_URL}/api/suppliers/${supplierId}`
        : `${API_URL}/api/suppliers`;

      const method = supplierId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/suppliers');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save supplier');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Basic Information */}
      <FormSection title="Basic Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Supplier Name" required>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Terminal/Vendor name"
            />
          </FormField>

          <FormField label="Display Name">
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Optional short name"
              className="form-input"
            />
          </FormField>

          <FormField label="Account Number">
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="Your account with supplier"
              className="form-input"
            />
          </FormField>

          <FormField label="Tax ID / EIN">
            <input
              type="text"
              name="taxId"
              value={formData.taxId}
              onChange={handleChange}
              placeholder="XX-XXXXXXX"
              className="form-input"
            />
          </FormField>

          {supplierId && (
            <FormField label="Status">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="w-5 h-5 text-amber bg-background border-panel-border focus:ring-amber rounded"
                />
                <span className="text-text-primary font-mono">Active</span>
              </label>
            </FormField>
          )}
        </div>
      </FormSection>

      {/* Contact Information */}
      <FormSection title="Contact Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Two fields, because reports sort by surname and one field cannot
              be split back apart reliably. Last name is the required half. */}
          <FormField label="Contact First Name">
            <input
              type="text"
              name="primaryContactFirstName"
              value={formData.primaryContactFirstName}
              onChange={handleChange}
              placeholder="First name"
              className="form-input"
            />
          </FormField>

          <FormField label="Contact Last Name">
            <input
              type="text"
              name="primaryContactLastName"
              value={formData.primaryContactLastName}
              onChange={handleChange}
              placeholder="Last name"
              className="form-input"
            />
          </FormField>

          <FormField label="Email">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@supplier.com"
              className="form-input"
            />
          </FormField>

          <FormField label="Phone">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className="form-input"
            />
          </FormField>

          <FormField label="Fax">
            <input
              type="tel"
              name="fax"
              value={formData.fax}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className="form-input"
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Website">
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://supplier.com"
                className="form-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Address */}
      <FormSection title="Address">
        <div className="grid grid-cols-1 gap-6">
          <FormField label="Street Address">
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Terminal Road"
              className="form-input"
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField label="City">
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                className="form-input"
              />
            </FormField>

            <FormField label="State">
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                maxLength={2}
                placeholder="TX"
                className="form-input uppercase"
              />
            </FormField>

            <FormField label="ZIP Code">
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                placeholder="12345"
                className="form-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Payment Terms */}
      <FormSection title="Payment Terms">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Payment Terms">
            <select
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleChange}
              className="form-input"
            >
              <option value="0">Due on Receipt (COD)</option>
              <option value="7">Net 7</option>
              <option value="15">Net 15</option>
              <option value="30">Net 30</option>
              <option value="45">Net 45</option>
              <option value="60">Net 60</option>
              <option value="90">Net 90</option>
            </select>
          </FormField>

          <FormField label="Credit Limit ($)">
            <input
              type="number"
              name="creditLimit"
              value={formData.creditLimit}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <FormField label="Internal Notes">
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Internal notes about this supplier..."
            className="form-input resize-none"
          />
        </FormField>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/suppliers')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : supplierId ? 'Update Supplier' : 'Create Supplier'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

/**
 * Form section with geometric panel styling
 */
function FormSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Form field with label
 */
function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
